import React, { useState, useEffect } from 'react';
import {
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import styles from './Plans.module.css';
import ColorPalette from '../../../colorpalette/ColorPalette';
import EnhancedAlert from '../../../components/alerts/EnhancedAlert';
import {
  GetSubscriptionPlansAPI,
  CreateStripeCheckoutSessionAPI,
  CreateStripeCustomerPortalAPI,
  UpdateCompanySubscriptionPlanAPI,
  GetPlanFitAPI
} from '../../../api/API';
import { trackStartTrial } from '../../../utils/analytics';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { isPastDue, isPastDueInGrace, formatGraceDeadline } from '../../../utils/pastDue';
import UpgradePlanModal from './UpgradePlanModal';
import PlanFitModal from './PlanFitModal';
import DowngradePlanModal from './DowngradePlanModal';
import { useRoleAccess } from '../../../hooks/useRoleAccess';
import { getWhatsAppSupportLink } from '../../../consts/ContactConfig';
import CompanyClosurePaywallBlock from '../../../components/company-closure/CompanyClosurePaywallBlock';
import { isClosureOpen } from '../../../components/company-closure/companyClosure';
import PlansHeader from './components/PlansHeader';
import PlanStatusBanner from './components/PlanStatusBanner';
import PlanCard from './components/PlanCard';
import PlanComparisonTable from './components/PlanComparisonTable';
import TrustStrip from './components/TrustStrip';
import { getPlanAction, getPlanCardState, getFeaturedPlanIndex } from './components/planCardState';
import { THIRD_PARTY_NOTE } from './components/planContent';
import { getWinbackDiscountText, getDiscountedPrice, getDiscountNote } from './components/winbackDiscount';
import DemoDataNote from '../../../components/demo-data/DemoDataNote';

/**
 * Destaques do card: a lista de features do plano no banco (system_subscription_plans),
 * sem a linha "Tudo do ..." (vira o intro do card). Asterisco no fim marca
 * integração de terceiro, contratada à parte.
 */
const getPlanHighlights = (plan) => (plan.features || [])
  .map((f) => (typeof f === 'object' ? (f.name || f.description || '') : String(f || '')).trim())
  .filter((text) => text && !/^tudo do/i.test(text))
  .map((text) => ({ text: text.replace(/\*+$/, ''), third: text.endsWith('*') }));

/**
 * Página de Planos
 *
 * Para usuários sem assinatura ativa: vai direto ao checkout Stripe.
 * Para assinantes:
 *   - Plano atual: selo "Seu plano" + botão desabilitado
 *   - Upgrade: botão de upgrade → abre modal de confirmação
 *   - Plano menor: responsável pela conta ou administrador. Se a conta passa do limite,
 *     PlanFitModal (escolha do que fica); se cabe, DowngradePlanModal. A troca é
 *     feita na hora por update_plan.php, sem novo checkout.
 */
const Plans = () => {
  const navigate = useNavigate();
  const {
    trialInfo,
    accessReason,
    hasActiveSubscription,
    isInActiveTrial,
    getTrialDaysRemaining,
    loading: subscriptionLoading,
    invalidateCache,
    refreshSubscription,
    lastActivePlan,
    minimumAllowedPlanOrder,
    closure,
    // Mesma resposta de subscription/company/get.php que o hook useSubscriptionPlans
    // buscava de novo (junto com uma segunda lista de planos, que consulta o Stripe):
    // o contexto já tem e se atualiza com refreshSubscription.
    subscription,
  } = useSubscription();

  // Estados para a view de checkout direto
  const [visiblePlans, setVisiblePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorTitle, setErrorTitle] = useState('Erro');
  const [errorDetails, setErrorDetails] = useState(null);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  // Oferta de reconquista do teste vencido há até 7 dias (null = não elegível)
  const [winbackDiscount, setWinbackDiscount] = useState(null);

  // Estados do modal de upgrade
  const [upgradePlan, setUpgradePlan] = useState(null);   // plano selecionado para upgrade
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);

  // Portal do Stripe — usado pelo aviso de pagamento pendente
  const [portalLoading, setPortalLoading] = useState(false);

  // Escolha do que fica: a clínica usa mais do que o plano escolhido permite
  // (ex.: trial com 2 profissionais indo para o Essencial). planFit guarda
  // { plan, fit } enquanto o modal está aberto.
  const [planFit, setPlanFit] = useState(null);
  const [planFitLoading, setPlanFitLoading] = useState(false);
  const [planFitError, setPlanFitError] = useState(null);

  // Troca para plano menor de quem já assina e cabe no plano novo
  const [downgradePlan, setDowngradePlan] = useState(null);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [downgradeError, setDowngradeError] = useState(null);

  // Plano menor com assinatura ativa: responsável pela conta ou administrador
  const { canAccess } = useRoleAccess();
  const canDowngrade = canAccess('subscription');

  // Verificar se o trial expirou:
  // Usa trialInfo diretamente (mais confiável) com fallback para accessReason
  const trialExpired =
    (trialInfo !== null && trialInfo?.is_trial_active === false && !!trialInfo?.trial_started_at) ||
    accessReason === 'trial_expired';

  // Escolher, trocar ou pagar o plano: responsável pela conta ou administrador.
  // A Equipe vê a mesma tela, com os botões bloqueados e o motivo na tela.
  const canActivateSubscription = canAccess('subscription');
  const PAYMENT_BLOCKED_TEXT = 'Só a pessoa responsável pela conta ou um administrador pode atualizar o pagamento.';
  // O cancelamento é desfeito no portal do Stripe
  const UNDO_CANCEL_BLOCKED_TEXT = 'Só a pessoa responsável pela conta ou um administrador pode desfazer o cancelamento.';

  const isSubscriber = hasActiveSubscription();
  const hasPlan = !!subscription?.has_active_plan;
  // Já assinou alguma vez (trial_used vira 1 no cadastro, então não serve para isso)
  const hadSubscription = !!lastActivePlan || !!subscription?.stripe_subscription_id;
  const isCanceling = hasPlan && subscription?.subscription_status?.toLowerCase() === 'canceling';
  // 'YYYY-MM-DD' -> 'DD/MM' sem passar por Date (evita virar o dia por fuso)
  const cancelDate = (() => {
    const [y, m, d] = String(subscription?.next_due_date || '').slice(0, 10).split('-');
    return y && m && d ? `${d}/${m}` : null;
  })();

  // Conta em encerramento e bloqueada: o bloco do encerramento substitui o aviso de trial expirado.
  const showClosureBlock = isClosureOpen(closure) && !isSubscriber;

  // Assinatura com pagamento pendente: aqui não adianta um novo checkout,
  // a saída é regularizar o cartão no portal do Stripe.
  const pastDue = isPastDue(subscription);
  const pastDueInGrace = isPastDueInGrace(subscription);
  const graceDeadline = formatGraceDeadline(subscription?.past_due_grace_ends_at);

  const handleOpenCustomerPortal = () => {
    if (portalLoading) return;
    setPortalLoading(true);

    CreateStripeCustomerPortalAPI((result) => {
      if (result && result.success && result.url) {
        window.location.href = result.url;
        return;
      }
      setPortalLoading(false);
      setError('Não foi possível abrir a página de pagamento. Tente novamente em instantes.');
      setErrorTitle('Pagamento');
      setErrorDetails(null);
    });
  };

  // Plano atual encontrado na lista de planos visíveis (só válido se has_active_plan)
  // Usa == (loose) para evitar falha de tipo string vs number entre frontend e backend
  const currentPlan = (subscription?.has_active_plan)
    // eslint-disable-next-line eqeqeq
    ? (visiblePlans.find(p => p.id == subscription?.subscription_plan_id) || null)
    : null;

  // Último plano ativo registrado no backend — usado para bloquear downgrade
  // lastActivePlan vem do SubscriptionContext (campo last_active_plan da API get.php)
  // Enriquecemos com os dados completos do plano (price, features, etc.) a partir de visiblePlans
  const lastPlan = lastActivePlan
    // eslint-disable-next-line eqeqeq
    ? (visiblePlans.find(p => p.id == lastActivePlan.id) || lastActivePlan)
    : null;


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Carregar planos via API direta (para checkout direto)
  useEffect(() => {
    setLoading(true);
    setError(null);

    GetSubscriptionPlansAPI((result) => {
      setLoading(false);

      if (result.success && result.plans && Array.isArray(result.plans)) {
        // Filtrar apenas planos visíveis
        const filtered = result.plans.filter(
          plan => plan.is_visible === 1 || plan.is_visible === true
        );

        if (filtered.length > 0) {
          setVisiblePlans(filtered);
          setWinbackDiscount(result.winback_discount || null);
        } else {
          setError('Nenhum plano disponível no momento. Por favor, tente novamente mais tarde.');
          setErrorTitle('Plano não encontrado');
        }
      } else {
        setError('Erro ao carregar os planos de assinatura. Por favor, tente novamente mais tarde.');
        setErrorTitle('Erro nos Planos');
        setErrorDetails(result.error || 'Não foi possível obter a lista de planos disponíveis');
      }
    });
  }, []);

  /**
   * Abre a sessão de checkout do Stripe (a clínica já cabe no plano)
   */
  const startCheckout = (plan) => {
    setProcessingPlanId(plan.id);
    setError(null);

    CreateStripeCheckoutSessionAPI(plan.id, (result) => {
      setProcessingPlanId(null);

      if (result && result.success && result.url) {
        trackStartTrial({
          plan: plan.name || 'plan',
          billingCycle: 'monthly',
          method: 'stripe_checkout'
        });
        window.location.href = result.url;
      } else if (result && result.code === 'OVER_PLAN_LIMITS') {
        // O backend recusou porque a clínica ainda não cabe no plano
        setError(result.message || 'Sua clínica usa mais do que este plano permite. Escolha o que fica antes de continuar.');
        setErrorTitle('Escolha o que fica');
        setErrorDetails(null);
      } else {
        const errorMessage = 'Erro ao iniciar checkout do Stripe.';
        const errorDetail = result ? (result.message || result.error) : 'Resposta inválida do servidor';
        setError(`${errorMessage} Por favor, tente novamente mais tarde.`);
        setErrorTitle('Erro no Checkout');
        setErrorDetails(errorDetail);
      }
    });
  };

  /**
   * Inicia o checkout Stripe para um plano (usuários sem assinatura).
   * Antes, confere se a clínica cabe nos limites do plano; se não cabe, abre o
   * passo de escolha do que fica (PlanFitModal) e só então segue para o Stripe.
   */
  const handleCheckout = (plan) => {
    if (!plan) return;

    setProcessingPlanId(plan.id);
    setError(null);

    GetPlanFitAPI(plan.id, (result) => {
      if (result && result.success && result.fits === false) {
        setProcessingPlanId(null);
        setPlanFitError(null);
        setPlanFit({ plan, fit: result });
        return;
      }
      // Cabe (ou a consulta falhou: o checkout confere de novo no backend)
      startCheckout(plan);
    });
  };

  /**
   * Segue para o checkout levando a escolha do que fica. Nada é inativado agora:
   * o backend guarda a escolha na sessão do Stripe e aplica quando o pagamento é
   * confirmado. Quem desiste do pagamento fica como estava.
   */
  const handleConfirmPlanFit = (keep) => {
    if (!planFit) return;
    const { plan } = planFit;

    // Quem já assina: troca na hora, sem checkout; o backend confere a escolha
    // antes de trocar no Stripe e só inativa o resto depois da troca.
    if (planFit.mode === 'downgrade') {
      setPlanFitLoading(true);
      setPlanFitError(null);
      submitDowngrade(plan, keep, (message) => {
        setPlanFitLoading(false);
        setPlanFitError(message);
      });
      return;
    }

    setPlanFitLoading(true);
    setPlanFitError(null);

    CreateStripeCheckoutSessionAPI(plan.id, (result) => {
      if (result && result.success && result.url) {
        trackStartTrial({
          plan: plan.name || 'plan',
          billingCycle: 'monthly',
          method: 'stripe_checkout'
        });
        window.location.href = result.url;
        return;
      }
      setPlanFitLoading(false);
      setPlanFitError(result?.message || result?.error || 'Não foi possível abrir o pagamento. Tente novamente.');
    }, keep);
  };

  const handleCancelPlanFit = () => {
    if (planFitLoading) return;
    setPlanFit(null);
    setPlanFitError(null);
  };

  /**
   * Plano "sob consulta": abre o WhatsApp da Gabriela com a mensagem pronta.
   */
  const openContactSales = (plan) => {
    const text = `Olá! Tenho interesse no plano ${plan.name} da Gabriela e gostaria de saber mais.`;
    window.open(getWhatsAppSupportLink(text), '_blank', 'noopener');
  };

  /**
   * Abre o modal de upgrade para o plano selecionado
   */
  const handleUpgradeClick = (plan) => {
    setUpgradeError(null);
    setUpgradePlan(plan);
  };

  /**
   * Confirma o upgrade de plano via API
   */
  const handleConfirmUpgrade = (plan) => {
    if (!plan) return;

    setUpgradeLoading(true);
    setUpgradeError(null);

    const handleResult = (result) => {
      setUpgradeLoading(false);

      if (result && result.success) {
        // Mostrar estado de sucesso no modal
        setUpgradeSuccess(true);
        // Invalidar cache e forçar refresh do contexto de assinatura
        // O navigate só ocorre após o refresh completar (via callback implícito no timeout)
        invalidateCache();
        // Aguarda o refresh completar antes de navegar:
        // refreshSubscription é assíncrono — damos 3s para a API retornar
        // e o planConfig/subscription do contexto serem atualizados antes de renderizar SubscriptionDetails
        refreshSubscription(true);
        setTimeout(() => {
          setUpgradePlan(null);
          setUpgradeSuccess(false);
          navigate('/subscription/details', { state: { upgraded: true } });
        }, 3000);
      } else {
        // Sanitizar mensagem de erro: se vier HTML ou texto muito longo (ex: stack trace PHP),
        // substituir por mensagem amigável
        const rawMsg = result?.message || (typeof result?.error === 'string' ? result.error : '');
        const isHtmlOrRaw = rawMsg.includes('<') || rawMsg.includes('Fatal error') || rawMsg.length > 300;
        const msg = isHtmlOrRaw || !rawMsg
          ? 'Não foi possível alterar o plano. Por favor, tente novamente ou entre em contato com o suporte.'
          : rawMsg;
        setUpgradeError(msg);
      }
    };
    // errorCallback: a resposta de erro chega inteira e a mensagem do servidor aparece
    UpdateCompanySubscriptionPlanAPI(plan.id, true, 'now', handleResult, null, handleResult);
  };

  /**
   * Plano menor para quem já assina: confere se a conta cabe. Não cabe → escolha do
   * que fica (PlanFitModal); cabe (ou a consulta falhou: o backend confere de novo)
   * → confirmação simples.
   */
  const handleDowngradeClick = (plan) => {
    setProcessingPlanId(plan.id);
    setError(null);

    GetPlanFitAPI(plan.id, (result) => {
      setProcessingPlanId(null);
      if (result && result.success && result.fits === false) {
        setPlanFitError(null);
        setPlanFit({ plan, fit: result, mode: 'downgrade' });
        return;
      }
      setDowngradeError(null);
      setDowngradePlan(plan);
    });
  };

  /**
   * Troca para o plano menor via update_plan.php (com a escolha do que fica, se houver).
   * onError(message) recebe a mensagem para o modal aberto.
   */
  const submitDowngrade = (plan, keep, onError) => {
    const handleResult = (result) => {
      if (result && result.success) {
        setPlanFit(null);
        setPlanFitLoading(false);
        setDowngradePlan(null);
        setDowngradeLoading(false);
        invalidateCache();
        refreshSubscription(true);
        if (result.plan_fit_applied === false) {
          // O plano trocou, mas o que ficou de fora não foi inativado
          setError(result.message);
          setErrorTitle('Plano trocado');
          setErrorDetails(null);
          return;
        }
        navigate('/subscription/details', { state: { upgraded: true } });
        return;
      }
      const rawMsg = result?.message || (typeof result?.error === 'string' ? result.error : '');
      const isHtmlOrRaw = rawMsg.includes('<') || rawMsg.includes('Fatal error') || rawMsg.length > 300;
      onError(isHtmlOrRaw || !rawMsg
        ? 'Não foi possível trocar de plano. Tente novamente ou fale com o suporte.'
        : rawMsg);
    };
    UpdateCompanySubscriptionPlanAPI(plan.id, true, 'now', handleResult, keep, handleResult);
  };

  const handleConfirmDowngrade = (plan) => {
    if (!plan) return;
    setDowngradeLoading(true);
    setDowngradeError(null);
    submitDowngrade(plan, null, (message) => {
      setDowngradeLoading(false);
      setDowngradeError(message);
    });
  };

  const handleCancelDowngrade = () => {
    if (downgradeLoading) return;
    setDowngradePlan(null);
    setDowngradeError(null);
  };

  /**
   * Fecha o modal de upgrade
   */
  const handleCancelUpgrade = () => {
    if (upgradeLoading) return;
    setUpgradePlan(null);
    setUpgradeError(null);
    setUpgradeSuccess(false);
  };

  /**
   * Handler unificado: roteado por ação do plano
   * - Com plano ativo: aplica lógica de upgrade/downgrade
   * - Sem plano ativo mas com histórico (lastPlan): bloqueia downgrade, permite checkout para igual/superior
   * - Sem histórico: vai direto ao checkout
   */
  const handleSelectPlan = (plan) => {
    // Plano "sob consulta" (config.contact_sales, hoje o Business): sem checkout
    // nem upgrade automático, a conversa começa no WhatsApp da Gabriela.
    if (plan.contact_sales) {
      openContactSales(plan);
      return;
    }

    // Equipe: o botão já vem bloqueado; a guarda evita qualquer chamada
    if (!canActivateSubscription) return;

    if (subscription?.has_active_plan) {
      // Cancelamento agendado: a troca só volta depois de desfazer o cancelamento
      if (isCanceling) return;
      // Assinante ativo: lógica de upgrade/downgrade
      const action = getPlanAction(plan, currentPlan);
      if (action === 'upgrade') {
        handleUpgradeClick(plan);
      } else if (action === 'downgrade' && canDowngrade) {
        handleDowngradeClick(plan);
      }
      // 'current' e plano menor sem permissão são tratados via disabled no botão
      return;
    }

    // Sem plano ativo mas com histórico: bloquear downgrade
    if (lastPlan && minimumAllowedPlanOrder !== null && plan.displayOrder < minimumAllowedPlanOrder) {
      // downgrade bloqueado — tratado via disabled no botão
      return;
    }

    // Trial puro ou plano igual/superior ao último: vai para checkout
    handleCheckout(plan);
  };

  const featuredPlanIndex = getFeaturedPlanIndex(visiblePlans);

  // Oferta de reconquista só para quem nunca assinou (o backend já filtra)
  const showWinbackDiscount = !!winbackDiscount && !isSubscriber && !hadSubscription;
  const planDiscount = (plan) => {
    const price = showWinbackDiscount ? getDiscountedPrice(plan, winbackDiscount) : null;
    return price === null ? null : { price, note: getDiscountNote(winbackDiscount.months) };
  };

  // Contexto comum aos cards (regras em components/planCardState.js)
  const cardContext = {
    isSubscriber,
    subscription,
    currentPlan,
    processingPlanId,
    canActivateSubscription,
    canDowngrade,
    isCanceling,
    minimumAllowedPlanOrder,
    hadSubscription,
  };

  // Estado de carregamento
  if (loading || subscriptionLoading) {
    return (
      <div className={styles.pageWrapper}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: ColorPalette.blueMain }} />
        </Box>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>

      {/* Modal de upgrade */}
      <UpgradePlanModal
        open={!!upgradePlan}
        currentPlan={currentPlan}
        newPlan={upgradePlan}
        loading={upgradeLoading}
        error={upgradeError}
        success={upgradeSuccess}
        onConfirm={handleConfirmUpgrade}
        onCancel={handleCancelUpgrade}
      />

      {/* Troca para plano menor de quem já assina, quando a conta já cabe */}
      <DowngradePlanModal
        open={!!downgradePlan}
        currentPlan={currentPlan}
        newPlan={downgradePlan}
        loading={downgradeLoading}
        error={downgradeError}
        onConfirm={handleConfirmDowngrade}
        onCancel={handleCancelDowngrade}
      />

      {/* Escolha do que fica antes do checkout (ou da troca, para quem já assina)
          de um plano menor que o uso atual */}
      {planFit && (
        <PlanFitModal
          key={planFit.plan.id}
          open
          fit={planFit.fit}
          loading={planFitLoading}
          error={planFitError}
          onConfirm={handleConfirmPlanFit}
          onCancel={handleCancelPlanFit}
          {...(planFit.mode === 'downgrade' ? {
            subtitle: 'Sua conta usa mais do que este plano permite. Marque o que continua ativo; o resto fica inativo quando a troca for confirmada. Nada é apagado.',
            confirmLabel: `Trocar para o ${planFit.plan.name}`,
            loadingLabel: 'Trocando...',
            showCreditNote: true,
          } : {})}
        />
      )}

      <div className={styles.page}>

        <PlansHeader
          situation={{
            hasPlan,
            planName: currentPlan?.name || subscription?.plan_name,
            isCanceling,
            pastDue,
            hadSubscription,
            showClosureBlock,
            trialExpired,
            inActiveTrial: isInActiveTrial(),
            trialDaysRemaining: getTrialDaysRemaining(),
            canActivateSubscription,
          }}
        />

        {/* Conta de teste com dados de exemplo: eles saem quando a assinatura começa */}
        <DemoDataNote>Ao assinar, os dados de exemplo são removidos.</DemoDataNote>

        {/* Encerramento da conta (LGPD): conta bloqueada com ciclo aberto */}
        <CompanyClosurePaywallBlock />

        {/* Pagamento pendente: regularizar no portal do Stripe */}
        {pastDue && (
          <PlanStatusBanner
            tone="error"
            title={pastDueInGrace ? 'Pagamento pendente' : 'Sua assinatura está com pagamento pendente'}
            text={
              (pastDueInGrace
                ? (graceDeadline
                  ? `Acesso mantido até ${graceDeadline}. Atualize a forma de pagamento até lá para continuar sem interrupção.`
                  : 'Acesso mantido por enquanto. Atualize a forma de pagamento para continuar sem interrupção.')
                : 'Regularize no portal para liberar o acesso.')
              + (!canActivateSubscription ? ` ${PAYMENT_BLOCKED_TEXT}` : '')
            }
            actionLabel={portalLoading ? 'Abrindo...' : 'Atualizar pagamento'}
            onAction={handleOpenCustomerPortal}
            actionDisabled={portalLoading || !canActivateSubscription}
            actionTitle={canActivateSubscription ? undefined : PAYMENT_BLOCKED_TEXT}
          />
        )}

        {/* Cancelamento agendado: data e como desfazer */}
        {!pastDue && isCanceling && (
          <PlanStatusBanner
            tone="warning"
            title="Sua assinatura está em cancelamento"
            text={
              (cancelDate ? `O plano segue ativo até ${cancelDate}. ` : 'O plano segue ativo até o fim do ciclo. ')
              + 'Para trocar de plano, desfaça o cancelamento primeiro.'
              + (!canDowngrade ? ` ${UNDO_CANCEL_BLOCKED_TEXT}` : '')
            }
            actionLabel={portalLoading ? 'Abrindo...' : 'Desfazer cancelamento'}
            onAction={handleOpenCustomerPortal}
            actionDisabled={portalLoading || !canDowngrade}
            actionTitle={canDowngrade ? undefined : UNDO_CANCEL_BLOCKED_TEXT}
          />
        )}

        {/* Oferta de reconquista: teste vencido há até 7 dias, aplicada no checkout */}
        {showWinbackDiscount && (
          <PlanStatusBanner
            tone="offer"
            title={`Volte com ${Number(winbackDiscount.percent)}% de desconto`}
            text={getWinbackDiscountText(winbackDiscount)}
          />
        )}

        {visiblePlans.length > 0 ? (
          <div className={styles.plansGrid}>
            {visiblePlans.map((plan, planIndex) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                state={getPlanCardState(plan, cardContext)}
                // "Mais escolhido" só para quem não assina
                featured={!isSubscriber && planIndex === featuredPlanIndex}
                highlights={getPlanHighlights(plan)}
                intro={planIndex > 0 ? `Tudo do ${visiblePlans[planIndex - 1].name}, e mais:` : null}
                onSelect={handleSelectPlan}
                discount={planDiscount(plan)}
              />
            ))}
          </div>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <Typography variant="h6" color="error">
              Nenhum plano disponível no momento.
            </Typography>
          </Box>
        )}

        {/* Erro global */}
        {error && (
          <EnhancedAlert
            severity="error"
            title={errorTitle}
            message={error}
            details={errorDetails}
          />
        )}

        <TrustStrip />

        {visiblePlans.length > 0 && (
          <PlanComparisonTable
            plans={visiblePlans}
            currentPlanId={hasPlan ? subscription?.subscription_plan_id : null}
          />
        )}

        {visiblePlans.length > 0 && (
          <p className={styles.footnote}>{THIRD_PARTY_NOTE}</p>
        )}

      </div>

    </div>
  );
};

export default Plans;
